import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
} from "@babackapp/customer-ui/components/header";
import { OptionsList } from "@babackapp/customer-ui/components/option";
import { RadioGroup } from "@babackapp/customer-ui/components/radio-group";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { useComputeReturnFees } from "@babackapp/return-widget-components/queries/use-compute-return-fees";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useCheckout } from "../hooks/use-checkout";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useStripeCheckout } from "../hooks/use-stripe-checkout";
import { useWidgetParams } from "../hooks/use-widget-params";
import { ShippingRateOption } from "./shipping-rate-option";
import { StripePaymentElement } from "./stripe-payment-element";

export function Checkout() {
  const { orderId, shopId } = useWidgetParams();
  const {
    financialSummary,
    totalAmountDue,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates,
    checkoutToken,
    needsPayment,
    shouldPayReshipping,
    shippingRateAmount,
    userPortalTexts,
  } = useCheckout();

  const stripe = useStripeCheckout(orderId, shopId, checkoutToken);
  const { data: returnFees } = useComputeReturnFees({ orderId, shopId });
  const t = useCustomText();

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("checkout.title")}</HeaderTitle>
        <HeaderDescription>
          {needsPayment
            ? t("checkout.descriptionWithPayment")
            : t("checkout.descriptionNoPayment")}
        </HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      {/* Confirm button first when no payment is needed — keeps it visible above the summary */}
      {!needsPayment && (
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Link to="../address">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button disabled={!canProceed} onClick={onProceed}>
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      )}

      <div className="w-full mt-4 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {financialSummary.refundTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.refundTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.exchangeTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.exchangeTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.exchangeTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.discountTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.discountTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.rebuyTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.rebuyTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                -{formatPrice(financialSummary.rebuyTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {returnFees && returnFees.current > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.returnFee")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(returnFees.current, returnFees.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {shouldPayReshipping && shippingRateAmount > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.reshippingFee")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(shippingRateAmount, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {needsPayment && totalAmountDue > 0 && (
            <>
              <Separator className="my-1" />
              <ReceiptLine>
                <ReceiptLineLabel className="font-medium text-foreground">
                  {t("financial.amountDue")}
                </ReceiptLineLabel>
                <ReceiptLineValue className="font-medium text-foreground">
                  {formatPrice(totalAmountDue, financialSummary.currency)}
                </ReceiptLineValue>
              </ReceiptLine>
            </>
          )}
        </div>

        {needsPayment && (
          <>
            {shippingRates.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader>{t("checkout.shippingRateTitle")}</SectionHeader>
                {isLoadingRates && (
                  <p className="text-sm text-muted-foreground">
                    {t("checkout.loadingRates")}
                  </p>
                )}
                {!isLoadingRates && (
                  <OptionsList>
                    <RadioGroup value={selectedRateId ?? ""} onValueChange={selectRate}>
                      {shippingRates.map((rate) => (
                        <ShippingRateOption
                          key={rate.handle}
                          rate={rate}
                          currency={financialSummary.currency}
                        />
                      ))}
                    </RadioGroup>
                  </OptionsList>
                )}
              </div>
            )}

            {checkoutToken && totalAmountDue > 0 && (
              <StripePaymentElement
                stripePromise={stripe.stripePromise}
                options={stripe.elementsOptions}
                onReady={stripe.onReady}
                onLoadError={stripe.onLoadError}
              />
            )}

            {stripe.errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{stripe.errorMessage}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>

      {/* Pay button when payment is needed — stays at the bottom */}
      {needsPayment && (
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Link to="../address">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button
              disabled={
                !canProceed ||
                (totalAmountDue > 0 && (!stripe.isReady || stripe.isSubmitting))
              }
              onClick={totalAmountDue > 0 ? stripe.onSubmit : onProceed}
            >
              <EnterIcon />
              {stripe.isSubmitting
                ? t("checkout.paymentPending")
                : totalAmountDue > 0
                  ? t("checkout.payButton", { amount: formatPrice(totalAmountDue, financialSummary.currency) })
                  : t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      )}
    </WidgetPage>
  );
}
